/* eslint-disable complexity */
/* eslint-disable max-statements */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import arrowBack from "./images/arrow-left.svg";
import { Label } from "@cars24/turbo-web/lib/label";
import { appRedirect, NUMBER } from "../../../constants/app-constants";
import { BodyText } from "@cars24/turbo-web/lib/body-text";
import { appUrl } from "../../../constants/url-constants";
import { Button } from "@cars24/turbo-web/lib/button";
import { IconButton } from "@cars24/turbo-web/lib/icon-button";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import {
    businesses,
    marketPlaceBaseUrl,
    rejectionText
} from "./constants";
import { convertJsonToQueryParams } from "../../../utils/helpers/convert-json-to-query-params";
import { Badge } from "@cars24/turbo-web/lib/badge";
import Skeleton from "./skeleton";
import { JOURNEY_STATUS } from "../post-inspection-marketplace-card/constants";
import { BottomSheet } from "@cars24/turbo-web/lib/bottom-sheet";
import { Heading } from "@cars24/turbo-web/lib/heading";
import rejectionSvg from "./images/rejection-svg.png";
import { ButtonStack } from "@cars24/turbo-web/lib/button-stack";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import { sendCustomGaEvents } from "../../../tracking";
import { Text } from "@cars24/turbo-web/lib/text";
import PostInspectionMarketplaceCardV2 from "../post-inspection-marketplace-card-v2";
import styles from "./styles.css";

const PostInspectionMarketplace = ({
    setShowC2c,
    secureToken,
    getC2cListingDataConnect,
    initiateListingConnect,
    userId,
    selectedCity,
    inspectionResponse,
    pickupDetail
}) => {
    const { tokens } = useTokens();
    const {
        carImage = "",
        appointmentId: leadId,
        token,
        make: makeDisplay,
        model: modelDisplay,
        variant: variantDisplay,
        maskedRegistrationNumber: vehicleRegNo,
        leadToken
    } = inspectionResponse || {};
    const { id = null } = selectedCity || {};

    const { prefillData: { cityId = null } = {} } = pickupDetail || {};
    const [ctaText, setCtaText] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [listingData, setListingData] = useState(null);
    const [showRejectionReasons, setShowRejectionReasons] = useState(false);

    const getC2cListingDetails = async () => {
        setLoading(true);
        try {
            const response = await getC2cListingDataConnect({
                appId: leadId,
                secureToken
            });
            setListingData({
                ...(response || {})
            });
            setCtaText(response?.statusDetail?.ctaText);
        } catch (error) {
            // eslint-disable-next-line no-console
            // console.error("Error while fetching listing Data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (leadId) {
            getC2cListingDetails();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadId]);

    const firePageViewEvent = async () => {
        await yieldToMain();
        sendCustomGaEvents({
            device_type: "M-WEB",
            user_id: userId,
            page_title: "Post Inspection Marketplace",
            event: "mp_c2c_c2b_entrypoint_view",
            eventLabel: "c2b_c2c_entrypoint_viewed",
            eventAction: "page_view",
            page_url: window.location.href,
            logged_in: true,
            cityId: cityId || id,
            city_name: selectedCity.name,
            appointment_id: leadId,
            eventCategory: "c2c_c2b_sell_options"
        });
    };

    useEffect(() => {
        firePageViewEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const redirect = _id => {
        const queryParam = convertJsonToQueryParams({
            listingId: _id,
            leadToken: leadToken || token,
            leadId
        });
        window.location.href = `/marketplace/listing/?${queryParam}`;
    };

    const handleListingRedirect = async () => {
        if (listingData && listingData.id) {
            redirect(listingData.id);
            return;
        }

        const { packageData } = listingData;

        if (packageData && packageData.packageId) {
            const data = {
                sellerId: userId,
                appointmentId: leadId,
                packageId: packageData.packageId,
                journeyStatus: JOURNEY_STATUS.PITCHED
            };
            try {
                const response = await initiateListingConnect(data, secureToken);
                if (response && response.id) {
                    redirect(response.id);
                }
            } catch (error) {
                // Handle error
            } finally {
                setLoading(false);
            }

            await yieldToMain();
            sendCustomGaEvents({
                event: "c2c_event",
                device_type: "M-WEB",
                user_id: userId,
                make: makeDisplay,
                model: modelDisplay,
                variant: variantDisplay,
                year: "",
                reg_number: vehicleRegNo,
                reg_city: "",
                reg_state: "",
                appointment_id: leadId,
                post_free_ad_cta_clicked_datetime: new Date(),
                plan_type: packageData.free ? "free" : "paid",
                plan_amount: packageData.finalAmount
            });
        } else {
            setLoading(false);
            // TODO: show bottomsheet selection when multiple packages available
        }
    };

    const handleStatsRedirect = () => {
        window.location.href = `${appUrl()}${marketPlaceBaseUrl}car-stats/?id=${leadId}`;
    };

    const handleBackClick = () => {
        window.location.href = `${appUrl()}${appRedirect.SELLUSEDCAR}`;
    };

    const handleCtaClick = async (currentBusiness) => {
        await yieldToMain();
        let event = "";
        switch (currentBusiness) {
        case businesses.CARS:
            setShowC2c(false);
            await yieldToMain();
            window.sessionStorage.setItem("b-key", "cs24");
            event = "mp_c2c_sell_options_c24_cta_click";
            break;
        case businesses.MP:
            event = "mp_c2c_sell_options_c2c_cta_click";
            if (listingData.journeyStatus === JOURNEY_STATUS.REJECTED) {
                setShowRejectionReasons(true);
                break;
            }
            if (!listingData.carListed) {
                handleListingRedirect();
            } else {
                handleStatsRedirect();
            }
            break;
        }

        await yieldToMain();
        sendCustomGaEvents({
            device_type: "M-WEB",
            user_id: userId,
            page_title: "Post Inspection Marketplace",
            event,
            eventLabel: `${event}, ${ctaText}`,
            eventAction: "cta_click",
            page_url: window.location.href,
            logged_in: true,
            cityId: cityId || id,
            city_name: selectedCity.name,
            appointment_id: leadId,
            eventCategory: "c2c_c2b_sell_options"
        });
    };

    const handleCardClick = async () => {
        const isSold =
      listingData.journeyStatus === JOURNEY_STATUS.SOLD ||
      listingData.journeyStatus === JOURNEY_STATUS.SOLD_WITHOUT_SPP;
        if (isSold) {
            handleStatsRedirect();
        }
        await yieldToMain();
        sendCustomGaEvents({
            device_type: "M-WEB",
            user_id: userId,
            page_title: "Post Inspection Marketplace",
            event: "mp_c2c_sell_on_c2c_card_click",
            eventLabel: "c2b_c2c_entrypoint_viewed",
            eventAction: "element_click",
            page_url: window.location.href,
            logged_in: true,
            cityId: cityId || id,
            city_name: selectedCity.name,
            appointment_id: leadId,
            eventCategory: "c2c_c2b_sell_options"
        });
    };

    const CarDetail = () => {
        return (
            <div styleName="styles.carDetail">

                <img src={carImage} alt={"car-image"} styleName="styles.carImg" />
                <div styleName="styles.details">
                    <div styleName={"styles.regNo"}>
                        <Text size={"150"} fontWeight="semibold">
                            {vehicleRegNo}
                        </Text>
                    </div>
                    {/* {vehicleRegNo && (
                        <Label level={NUMBER.THREE} fontWeight="medium">
                            {vehicleRegNo}
                        </Label>

                    )} */}
                    <BodyText level={NUMBER.ONE} isBold>
                        {makeDisplay || ""} {modelDisplay || ""} {variantDisplay || ""}
                    </BodyText>
                </div>
            </div>
        );
    };

    const SellToCars24Card = () => {
        return (
            <div
                styleName="styles.sellToCar24Card"
            >
                <div styleName="styles.sellToCar24CardContent">
                    <div styleName="styles.sellToCar24CardHeader">
                        <Text size="450" fontWeight="semibold">
                            Sell to CARS24
                        </Text>
                        <div>
                            <Badge
                                label="Active"
                                variant="success"
                                type="light"
                                // rounded="right"
                            />
                        </div>
                    </div>
                    <Text
                        level="200"
                        color={tokens.text_default_text_secondary}
                    >
                        Make a quick sell with a hassle-free process and get paid instantly.
                    </Text>
                    <Button
                        label="Continue selling"
                        onClick={() => handleCtaClick(businesses.CARS)}
                        variant="primary"
                        size="small"
                    />
                </div>
            </div>
        );
    };

    return (
        <div styleName="styles.pageContainer">
            <div styleName="styles.pageHeader">
                <IconButton onClick={handleBackClick}>
                    <img src={arrowBack} alt="back" styleName="styles.backIcon" />
                </IconButton>
                <Label level={NUMBER.TWO} fontWeight="semibold">
                    Sell your car
                </Label>
            </div>
            {isLoading ? (
                <Skeleton />
            ) : (
                <>
                    <CarDetail />
                    <div styleName="styles.cardContainer">
                        <SellToCars24Card />
                        <PostInspectionMarketplaceCardV2
                            handleCardClick={handleCardClick}
                            listingData={listingData}
                            loading={isLoading}
                            ctaText={ctaText}
                            handleCtaClick={() => handleCtaClick(businesses.MP)}
                            leadId={leadId}
                            selectedCity={selectedCity}
                        />
                    </div>
                </>
            )}
            <BottomSheet
                isOpen={showRejectionReasons}
                overlayColor={tokens.background_surface_overlay_primary}
                onClose={() => setShowRejectionReasons(false)}
                height={"fit-content"}
            >
                <div styleName="styles.flexColumn styles.rejectionReasonContainer">
                    <div styleName="styles.flexColumn styles.contentWrapper">
                        <div styleName="styles.flexColumn styles.mainContent">
                            <div>
                                <Heading level={4} isBold>
                                    {rejectionText.title}
                                </Heading>
                                <BodyText level={2} color={tokens.text_default_text_secondary}>
                                    {rejectionText.description}
                                </BodyText>
                                <img
                                    src={rejectionSvg}
                                    alt="rejection"
                                    height={"auto"}
                                    width={"100%"}
                                />
                            </div>
                            <ul styleName="styles.reasonList">
                                {((listingData && listingData.qcRejectReasons) || []).map(
                                    (text, index) => {
                                        return (
                                            <li key={index}>
                                                <BodyText
                                                    level={2}
                                                    color={tokens.text_default_text_secondary}
                                                >
                                                    {text}
                                                </BodyText>
                                            </li>
                                        );
                                    }
                                )}
                            </ul>
                        </div>
                        <div styleName="styles.flexColumn styles.subContent">
                            <Label level={3} fontWeight="semibold">
                What happens next?
                            </Label>
                            <div styleName="styles.reasonInfo">
                                <BodyText level={2} color={tokens.text_default_text_secondary}>
                  Your can continue to sell your car to CARS24 through the
                  auction process.
                                </BodyText>
                            </div>
                        </div>
                    </div>
                    <div>
                        <ButtonStack layout="vertical">
                            <Button
                                label="I understand"
                                variant="primary"
                                size="large"
                                onClick={() => {
                                    setShowRejectionReasons(false);
                                }}
                            />
                        </ButtonStack>
                    </div>
                </div>
            </BottomSheet>
        </div>
    );
};

PostInspectionMarketplace.propTypes = {
    setShowC2c: PropTypes.func,
    carImage: PropTypes.string,
    secureToken: PropTypes.string,
    getC2cListingDataConnect: PropTypes.func,
    initiateListingConnect: PropTypes.func,
    userId: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    mobile: PropTypes.string,
    history: PropTypes.object,
    selectedCity: PropTypes.object,
    inspectionResponse: PropTypes.object,
    pickupDetail: PropTypes.object
};

export default PostInspectionMarketplace;
